import { useState, useEffect } from 'react';
import Api from '../Network';
import { message } from 'antd';
import moment from 'moment';
import type { ReportPermissionContext } from '../constants/reportPermissionContext';

export interface SalesByRepresentative {
  sales_representative_id: number;
  sales_representative_name: string;
  total_sales: number;
  order_count: number;
}

export interface SalesByRepresentativesData {
  sales_by_representatives: SalesByRepresentative[];
  date_range: {
    start_date: string;
    end_date: string;
  };
  total_sales: number;
}

export interface UseSalesByRepresentativesReturn {
  data: SalesByRepresentativesData | null;
  loading: boolean;
  error: string | null;
  refetch: (startDate?: string, endDate?: string) => Promise<void>;
}

export const useSalesByRepresentatives = (
  startDate?: string,
  endDate?: string,
  enabled = true,
  permissionContext?: ReportPermissionContext
): UseSalesByRepresentativesReturn => {
  const [data, setData] = useState<SalesByRepresentativesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSalesByRepresentatives = async (start?: string, end?: string) => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Use provided dates or defaults to current month
      // Format dates correctly to YYYY-MM-DD
      const startDateParam = start
        ? moment(start).format('YYYY-MM-DD')
        : moment().startOf('month').format('YYYY-MM-DD');
      const endDateParam = end
        ? moment(end).format('YYYY-MM-DD')
        : moment().endOf('month').format('YYYY-MM-DD');

      const response = await Api.get('/api/admin/dashboard/sales-by-representatives', {
        params: {
          start_date: startDateParam,
          end_date: endDateParam,
          ...(permissionContext ? { permission_context: permissionContext } : {}),
        },
      });

      if (
        response &&
        typeof response.status === 'boolean' &&
        response.status === true &&
        response.data
      ) {
        setData(response.data);
        setLoading(false);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load sales by representatives');
      setLoading(false);
      if (error.response?.status !== 200) {
        message.error('Failed to load sales by representatives');
      }
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchSalesByRepresentatives(startDate, endDate);
    } else {
      setLoading(false);
      setData(null);
    }
  }, [startDate, endDate, enabled, permissionContext]);

  return {
    data,
    loading,
    error,
    refetch: fetchSalesByRepresentatives,
  };
};
